import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface Ctx {
  items: BreadcrumbItem[];
  setItems: (items: BreadcrumbItem[]) => void;
}

const BreadcrumbCtx = createContext<Ctx>({ items: [], setItems: () => {} });

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [items, setItemsState] = useState<BreadcrumbItem[]>([]);
  const setItems = useCallback((v: BreadcrumbItem[]) => setItemsState(v), []);
  return (
    <BreadcrumbCtx.Provider value={{ items, setItems }}>
      {children}
    </BreadcrumbCtx.Provider>
  );
}

/** Call this at the top of a page component to set the breadcrumb trail. */
export function useBreadcrumbs(items: BreadcrumbItem[]) {
  const { setItems } = useContext(BreadcrumbCtx);
  const key = JSON.stringify(items);
  useEffect(() => {
    setItems(JSON.parse(key) as BreadcrumbItem[]);
    return () => setItems([]);
  }, [key, setItems]);
}

/** Used by Layout to read the current breadcrumb items. */
export function useBreadcrumbItems() {
  return useContext(BreadcrumbCtx).items;
}
